import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { FeedbackDialog } from '../components/FeedbackDialog';
import { Textarea } from '../components/ui/textarea';
import { CheckCircle, XCircle, HelpCircle, Loader2, FileText, Car, Activity, Wifi, WifiOff, RefreshCw } from 'lucide-react';
import { FileUpload, FileAttachmentList } from '../components/FileUpload';
import VehicleImage from '../components/ui/VehicleImage';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Helper function to get auth headers
const getAuthHeaders = () => {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

export const DiagnosisSession = () => {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  
  const [session, setSession] = useState(null);
  const [currentStep, setCurrentStep] = useState(null);
  const [loading, setLoading] = useState(false);
  const [note, setNote] = useState('');
  const [actualValues, setActualValues] = useState([{ value: '', unit: '' }]); // Array für mehrere Messwerte
  const [valueAssessment, setValueAssessment] = useState(null); // KI-Beurteilung
  const [isComplete, setIsComplete] = useState(false);
  const [finalDiagnosis, setFinalDiagnosis] = useState(null);
  const [userSubscription, setUserSubscription] = useState(null);
  const [availableUnits, setAvailableUnits] = useState([]); // Dynamische Einheiten aus Sollwerten
  const [reportPdfUrl, setReportPdfUrl] = useState(null);
  const [showFeedbackDialog, setShowFeedbackDialog] = useState(false); // PDF URL nach Generierung
  const [sessionAttachments, setSessionAttachments] = useState([]); // Uploaded files
  
  // Disclaimer State
  const [showDisclaimerModal, setShowDisclaimerModal] = useState(false);
  const [disclaimerText, setDisclaimerText] = useState('');
  const [disclaimerAccepted, setDisclaimerAccepted] = useState(false);
  const [disclaimerCheckbox, setDisclaimerCheckbox] = useState(false);
  
  // Live OBD State
  const [obdAgentStatus, setObdAgentStatus] = useState('checking'); // 'checking', 'connected', 'disconnected'
  const [liveDataLoading, setLiveDataLoading] = useState(false);
  const [liveData, setLiveData] = useState(null);
  const [liveDtcData, setLiveDtcData] = useState(null);

  useEffect(() => {
    const initDiagnosis = async () => {
      console.log('[DiagnosisSession] Initializing diagnosis session:', sessionId);
      // Load session first
      await fetchSession();
      // Then load subscription (needed for features)
      await fetchUserSubscription();
      // Then check disclaimer (needs session data)
      await fetchDisclaimerSettings();
      // Check if there's a current step, otherwise get next step
      await loadCurrentOrNextStep();
      // Check OBD agent connection status
      await checkObdAgentStatus();
    };
    
    initDiagnosis();
  }, [sessionId]);

  // Show feedback dialog when diagnosis is complete
  useEffect(() => {
    if (session && session.status === 'done' && !showFeedbackDialog) {
      // Check if already submitted or skipped
      const submitted = localStorage.getItem(`feedback_submitted_${sessionId}`);
      const skipped = localStorage.getItem(`feedback_skipped_${sessionId}`);
      
      if (submitted || skipped) {
        // Never ask again for this session
        return;
      }
      
      // Check if dismissed (wegklickt)
      const dismissed = localStorage.getItem(`feedback_dismissed_${sessionId}`);
      const now = Date.now();
      
      if (dismissed) {
        const dismissedTime = parseInt(dismissed);
        const timeSince = now - dismissedTime;
        
        // Ask again after 5 minutes if dismissed
        if (timeSince < 5 * 60 * 1000) {
          return;
        }
      }
      
      const timer = setTimeout(() => {
        setShowFeedbackDialog(true);
      }, 8000); // Show after 8 seconds
      
      // Cleanup timer on unmount
      return () => clearTimeout(timer);
    }
  }, [session?.status, sessionId, showFeedbackDialog]);

  const fetchUserSubscription = async (forceRefresh = false) => {
    try {
      // Add cache buster for force refresh
      const url = forceRefresh 
        ? `${API}/subscriptions/my-subscription?_t=${Date.now()}` 
        : `${API}/subscriptions/my-subscription`;
        
      const response = await axios.get(url, {
        headers: getAuthHeaders()
      });
      console.log('[Subscription] ===================');
      console.log('[Subscription] Plan:', response.data?.plan?.name);
      console.log('[Subscription] Plan ID:', response.data?.plan?.id);
      console.log('[Subscription] file_uploads:', response.data?.plan?.features?.file_uploads);
      console.log('[Subscription] advanced_diagnosis:', response.data?.plan?.features?.advanced_diagnosis);
      console.log('[Subscription] live_obd_enabled:', response.data?.plan?.features?.live_obd_enabled);
      console.log('[Subscription] All features:', response.data?.plan?.features);
      console.log('[Subscription] ===================');
      setUserSubscription(response.data);
    } catch (error) {
      console.error('[Subscription] ❌ Failed to fetch:', error);
    }
  };
  
  // Check OBD Agent Connection Status
  const checkObdAgentStatus = async () => {
    try {
      const response = await axios.get(`${API}/live-obd/status`, {
        headers: getAuthHeaders()
      });
      
      if (response.data?.connection_status === 'connected' || response.data?.sessions?.some(s => s.state === 'connected')) {
        setObdAgentStatus('connected');
      } else {
        setObdAgentStatus('disconnected');
      }
    } catch (error) {
      console.log('[OBD] Status check failed:', error.message);
      setObdAgentStatus('disconnected');
    }
  };
  
  // Request Live DTC Data from OBD Agent
  const requestLiveDtcData = async () => {
    if (obdAgentStatus !== 'connected') {
      toast.error('OBD-Agent nicht verbunden. Bitte verbinden Sie zuerst den Agent.');
      return;
    }
    
    setLiveDataLoading(true);
    try {
      const response = await axios.post(`${API}/live-obd/diagnosis/read-dtcs`, {}, {
        headers: getAuthHeaders()
      });
      
      if (response.data?.success) {
        setLiveDtcData(response.data.data);
        toast.success('Fehlercodes erfolgreich ausgelesen!');
        
        // Add live data to note for AI context
        const dtcInfo = response.data.data?.dtcs?.length > 0 
          ? `Live DTCs vom Fahrzeug: ${response.data.data.dtcs.map(d => d.code).join(', ')}`
          : 'Keine aktiven DTCs im Fahrzeug gefunden.';
        setNote(prev => prev ? `${prev}\n\n${dtcInfo}` : dtcInfo);
      } else {
        toast.error(response.data?.message || 'Fehler beim Auslesen der DTCs');
      }
    } catch (error) {
      console.error('DTC read error:', error);
      toast.error(error.response?.data?.detail || 'Fehler beim Auslesen der Fehlercodes');
    } finally {
      setLiveDataLoading(false);
    }
  };
  
  // Request Live Sensor Data from OBD Agent
  const requestLiveSensorData = async () => {
    if (obdAgentStatus !== 'connected') {
      toast.error('OBD-Agent nicht verbunden. Bitte verbinden Sie zuerst den Agent.');
      return;
    }
    
    setLiveDataLoading(true);
    try {
      // Request common PIDs: RPM, Speed, Coolant Temp, Intake Temp, Throttle Position
      const response = await axios.post(`${API}/live-obd/diagnosis/read-live-data`, {
        pids: ['010C', '010D', '0105', '010F', '0111'] // Common PIDs
      }, {
        headers: getAuthHeaders()
      });
      
      if (response.data?.success) {
        setLiveData(response.data.data);
        toast.success('Live-Daten erfolgreich ausgelesen!');
        
        // Format live data for AI context
        const liveInfo = formatLiveDataForNote(response.data.data);
        setNote(prev => prev ? `${prev}\n\n${liveInfo}` : liveInfo);
      } else {
        toast.error(response.data?.message || 'Fehler beim Auslesen der Live-Daten');
      }
    } catch (error) {
      console.error('Live data read error:', error);
      toast.error(error.response?.data?.detail || 'Fehler beim Auslesen der Live-Daten');
    } finally {
      setLiveDataLoading(false);
    }
  };
  
  // Format live data for note
  const formatLiveDataForNote = (data) => {
    if (!data || !data.values) return 'Keine Live-Daten verfügbar.';
    
    const formatted = Object.entries(data.values)
      .map(([pid, value]) => {
        const pidNames = {
          '010C': 'Drehzahl',
          '010D': 'Geschwindigkeit',
          '0105': 'Kühlmitteltemperatur',
          '010F': 'Ansauglufttemperatur',
          '0111': 'Drosselklappenstellung'
        };
        return `${pidNames[pid] || pid}: ${value.value} ${value.unit || ''}`;
      })
      .join(', ');
    
    return `Live-Sensordaten vom Fahrzeug: ${formatted}`;
  };

  const fetchDisclaimerSettings = async () => {
    try {
      console.log('[Disclaimer] Fetching disclaimer settings...');
      const response = await axios.get(`${API}/admin/settings/disclaimers/public`);
      console.log('[Disclaimer] Response:', response.data);
      
      // API returns {de: "...", en: "..."} - use German version
      const disclaimerText = response.data?.de || response.data?.diagnosis_disclaimer;
      console.log('[Disclaimer] Text found:', !!disclaimerText);
      
      if (disclaimerText) {
        setDisclaimerText(disclaimerText);
        
        // Prüfe, ob Disclaimer bereits für diese Session akzeptiert wurde
        const sessionResponse = await axios.get(`${API}/sessions/${sessionId}`, {
          headers: getAuthHeaders()
        });
        const disclaimerAcceptedInSession = sessionResponse.data?.disclaimer_accepted;
        console.log('[Disclaimer] Already accepted for this session:', disclaimerAcceptedInSession);
        console.log('[Disclaimer] Session data:', sessionResponse.data);
        
        // Zeige Modal wenn NICHT akzeptiert (strict check: muss === true sein)
        if (disclaimerAcceptedInSession !== true) {
          console.log('[Disclaimer] ✅ Showing modal - disclaimer not accepted');
          setShowDisclaimerModal(true);
        } else {
          console.log('[Disclaimer] ⏭️ Skipping modal - already accepted');
          setDisclaimerAccepted(true);
        }
      } else {
        console.log('[Disclaimer] ⚠️ No disclaimer text configured - proceeding without modal');
        setDisclaimerAccepted(true);
      }
    } catch (error) {
      console.error('[Disclaimer] ❌ Failed to fetch disclaimer:', error);
      // Wenn kein Disclaimer konfiguriert ist, fortfahren
      setDisclaimerAccepted(true);
    }
  };

  const handleAcceptDisclaimer = async () => {
    if (!disclaimerCheckbox) {
      toast.error('Bitte bestätigen Sie, dass Sie den Haftungsausschluss gelesen haben');
      return;
    }

    try {
      // Speichere Zustimmung in Session
      await axios.put(`${API}/sessions/${sessionId}`, {
        disclaimer_accepted: true,
        disclaimer_accepted_at: new Date().toISOString()
      }, {
        headers: getAuthHeaders()
      });

      setDisclaimerAccepted(true);
      setShowDisclaimerModal(false);
      toast.success('Haftungsausschluss akzeptiert');
    } catch (error) {
      console.error('Failed to save disclaimer acceptance:', error);
      toast.error('Fehler beim Speichern der Zustimmung');
    }
  };

  // Extrahiere verfügbare Einheiten aus den Sollwerten
  const extractAvailableUnits = (step) => {
    const units = new Set();
    
    // Standard-Einheiten als Fallback
    const defaultUnits = ['V', 'A', 'Ω', 'kΩ', 'mV', 'mA', 'ml', 'bar', 'Hz', '°C', '%'];
    
    // Aus thresholds
    if (step?.thresholds?.unit) {
      units.add(step.thresholds.unit);
    }
    
    // Aus expected Text extrahieren
    if (step?.expected) {
      const text = step.expected;
      // Regex für Einheiten: findet "12V", "1.5 Ω", "20 Ohm", etc.
      const unitPatterns = [
        /(\d+\.?\d*)\s*(V|Volt)/gi,
        /(\d+\.?\d*)\s*(A|Ampere)/gi,
        /(\d+\.?\d*)\s*(Ω|Ohm|kΩ|mΩ)/gi,
        /(\d+\.?\d*)\s*(mV|Millivolt)/gi,
        /(\d+\.?\d*)\s*(mA|Milliampere)/gi,
        /(\d+\.?\d*)\s*(ml|Milliliter)/gi,
        /(\d+\.?\d*)\s*(bar|Bar)/gi,
        /(\d+\.?\d*)\s*(Hz|Hertz)/gi,
        /(\d+\.?\d*)\s*(°C|Grad)/gi,
        /(\d+\.?\d*)\s*(%|Prozent)/gi
      ];
      
      unitPatterns.forEach(pattern => {
        const matches = [...text.matchAll(pattern)];
        matches.forEach(match => {
          let unit = match[2];
          // Normalisiere Einheiten
          if (unit.toLowerCase().includes('volt')) unit = 'V';
          else if (unit.toLowerCase().includes('ampere')) unit = 'A';
          else if (unit.toLowerCase().includes('ohm')) unit = 'Ω';
          else if (unit.toLowerCase().includes('grad')) unit = '°C';
          units.add(unit);
        });
      });
    }
    
    // Wenn keine Einheiten gefunden, verwende Defaults
    const finalUnits = units.size > 0 ? Array.from(units) : defaultUnits;
    setAvailableUnits(finalUnits);
  };

  // Füge eine weitere Messzeile hinzu
  const addMeasurementRow = () => {
    setActualValues([...actualValues, { value: '', unit: availableUnits[0] || '' }]);
  };

  // Entferne eine Messzeile
  const removeMeasurementRow = (index) => {
    if (actualValues.length > 1) {
      setActualValues(actualValues.filter((_, i) => i !== index));
    }
  };

  // Update eines Messwertes
  const updateMeasurementValue = (index, field, value) => {
    const updated = [...actualValues];
    updated[index][field] = value;
    setActualValues(updated);
  };

  // Parse Reparaturanleitung in strukturierte Abschnitte
  const parseRepairGuide = (repairText) => {
    if (!repairText) return null;

    // Bereinige den Text von JSON und Markdown
    let cleanText = repairText;
    
    // Entferne JSON Code Blocks
    cleanText = cleanText.replace(/```json/g, '').replace(/```/g, '');
    
    // Entferne JSON-Struktur wenn vorhanden
    if (cleanText.trim().startsWith('{') && cleanText.trim().endsWith('}')) {
      try {
        const jsonData = JSON.parse(cleanText);
        // Extrahiere relevante Felder aus JSON
        const parts = [];
        if (jsonData.procedure) parts.push(jsonData.procedure);
        if (jsonData.repair) parts.push(jsonData.repair);
        if (jsonData.instruction) parts.push(jsonData.instruction);
        if (jsonData.steps) parts.push(Array.isArray(jsonData.steps) ? jsonData.steps.join('\n') : jsonData.steps);
        cleanText = parts.join('\n\n');
      } catch (e) {
        // Falls Parsing fehlschlägt, entferne manuell JSON-Zeichen
        cleanText = cleanText
          .replace(/[{}]/g, '')
          .replace(/"([^"]+)":/g, '$1:')
          .replace(/",/g, '')
          .replace(/"/g, '');
      }
    }

    const sections = {
      tools: [],
      parts: [],
      safety: [],
      steps: [],
      hints: [],
      other: []
    };

    // Teile den Text in Zeilen
    const lines = cleanText.split('\n').filter(line => {
      const trimmed = line.trim();
      // Filtere leere Zeilen und JSON-Artefakte
      return trimmed && 
             !trimmed.match(/^[\[\]{},:]+$/) &&
             !trimmed.startsWith('"') && 
             !trimmed.endsWith('",') &&
             !trimmed.includes('instruction":') &&
             !trimmed.includes('procedure":') &&
             !trimmed.includes('type":') &&
             !trimmed.includes('thresholds":');
    });

    let currentSection = 'other';

    lines.forEach(line => {
      const trimmedLine = line.trim();
      const lowerLine = trimmedLine.toLowerCase();
      
      // Ignoriere JSON-Artefakte
      if (lowerLine.includes('json') || lowerLine.startsWith('"') || trimmedLine.match(/^[{}\[\],]+$/)) {
        return;
      }
      
      // Erkenne Überschriften
      if (lowerLine.includes('sicherheit') || lowerLine.includes('warnung') || lowerLine.includes('achtung')) {
        currentSection = 'safety';
        return;
      }
      if (lowerLine.includes('werkzeug') || lowerLine.includes('tool')) {
        currentSection = 'tools';
        return;
      }
      if (lowerLine.includes('teil') || lowerLine.includes('ersatzteil') || lowerLine.includes('bauteil')) {
        currentSection = 'parts';
        return;
      }
      if (lowerLine.includes('schritt') || lowerLine.includes('reparatur') || lowerLine.includes('vorgehen')) {
        currentSection = 'steps';
        return;
      }
      if (lowerLine.includes('hinweis') || lowerLine.includes('tipp') || lowerLine.includes('beachten')) {
        currentSection = 'hints';
        return;
      }

      // Automatische Erkennung basierend auf Inhalt
      if (trimmedLine.match(/^\d+\./)) {
        // Nummerierte Liste = Schritte
        sections.steps.push(trimmedLine);
      } else if (trimmedLine.match(/^[-•*]/)) {
        // Bullet Points
        if (lowerLine.includes('mm') || lowerLine.includes('schlüssel') || 
            lowerLine.includes('schraubendreher') || lowerLine.includes('zange') || 
            lowerLine.includes('multimeter') || lowerLine.includes('drehmoment')) {
          sections.tools.push(trimmedLine);
        } else if (lowerLine.includes('⚠️') || lowerLine.includes('achtung') || 
                   lowerLine.includes('vorsicht') || lowerLine.includes('gefahr')) {
          sections.safety.push(trimmedLine);
        } else {
          sections[currentSection].push(trimmedLine);
        }
      } else if (trimmedLine.length > 10) {
        // Längere Zeilen
        sections[currentSection].push(trimmedLine);
      }
    });

    return sections;
  };

  const fetchSession = async () => {
    try {
      const response = await axios.get(`${API}/sessions/${sessionId}`, {
        headers: getAuthHeaders()
      });
      setSession(response.data);
    } catch (error) {
      toast.error('Fehler beim Laden der Session');
    }
  };

  const loadCurrentOrNextStep = async () => {
    setLoading(true);
    try {
      // First check if there's a current/pending step
      const currentStepResponse = await axios.get(`${API}/sessions/${sessionId}/current-step`, {
        headers: getAuthHeaders()
      });
      
      console.log('[DiagnosisSession] Current step response:', currentStepResponse.data);
      
      if (currentStepResponse.data.current_step) {
        // There's a pending step, load it
        const step = currentStepResponse.data.current_step;
        console.log('[DiagnosisSession] Loading pending step:', step.id);
        setCurrentStep(step);
        extractAvailableUnits(step);
      } else {
        // No pending step - either no steps at all, or all are complete
        // Get the next step (or complete the diagnosis)
        console.log('[DiagnosisSession] No pending step, getting next step');
        await getNextStep();
      }
    } catch (error) {
      console.error('[DiagnosisSession] Error loading current step:', error);
      toast.error('Fehler beim Laden der Diagnose');
    } finally {
      setLoading(false);
    }
  };

  const getNextStep = async () => {
    setLoading(true);
    try {
      const response = await axios.post(`${API}/sessions/next-step`, {
        session_id: sessionId
      }, {
        headers: getAuthHeaders()
      });
      
      if (response.data.is_complete) {
        setIsComplete(true);
        setFinalDiagnosis({
          diagnosis: response.data.final_diagnosis,
          repair: response.data.repair_guide
        });
        // Update session status to trigger feedback dialog
        setSession(prev => prev ? { ...prev, status: 'done' } : prev);
      } else {
        setCurrentStep(response.data.step);
        // Extrahiere verfügbare Einheiten aus dem Schritt
        extractAvailableUnits(response.data.step);
      }
    } catch (error) {
      toast.error('Fehler beim Laden des nächsten Schritts');
    } finally {
      setLoading(false);
    }
  };

  const handleResult = async (result) => {
    if (!currentStep) return;

    try {
      // Formatiere alle Messwerte als String
      const formattedValues = actualValues
        .filter(m => m.value.trim())
        .map(m => `${m.value.trim()} ${m.unit}`)
        .join(', ');

      await axios.post(`${API}/sessions/steps/${currentStep.id}/result`, {
        result: result,
        note: note,
        actual_value: formattedValues, // Alle Messwerte mitschicken
        assessment_status: valueAssessment?.status || null,
        assessment_message: valueAssessment?.assessment || null,
        attachments: []
      }, {
        headers: getAuthHeaders()
      });
      
      setNote('');
      setActualValues([{ value: '', unit: '' }]);
      setValueAssessment(null);
      toast.success('Ergebnis gespeichert');
      getNextStep();
    } catch (error) {
      toast.error('Fehler beim Speichern');
    }
  };

  const assessActualValue = async () => {
    // Prüfe ob mindestens ein Wert eingetragen ist
    const hasValues = actualValues.some(m => m.value.trim());
    if (!hasValues || !currentStep) return;

    try {
      setLoading(true);
      
      // Formatiere alle Messwerte als Array
      const measurements = actualValues
        .filter(m => m.value.trim())
        .map(m => ({
          value: `${m.value.trim()} ${m.unit}`,
          unit: m.unit
        }));

      const response = await axios.post(`${API}/sessions/assess-value`, {
        step_id: currentStep.id,
        measurements: measurements, // Array von Messungen
        expected_value: currentStep.expected,
        thresholds: currentStep.thresholds
      }, {
        headers: getAuthHeaders()
      });
      
      setValueAssessment(response.data);
      toast.success('Werte beurteilt');
    } catch (error) {
      toast.error('Fehler bei der Beurteilung');
      console.error('Assessment error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateReport = async () => {
    try {
      setLoading(true);
      
      // Generate report first
      await axios.post(`${API}/sessions/${sessionId}/report`, {}, {
        headers: getAuthHeaders()
      });
      
      // Now download with proper authentication
      const downloadResponse = await axios.get(`${API}/sessions/${sessionId}/report/download`, {
        headers: getAuthHeaders(),
        responseType: 'blob'
      });
      
      // Create download link
      const blob = new Blob([downloadResponse.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `diagnose_report_${sessionId}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success('PDF Report wurde heruntergeladen!');
    } catch (error) {
      console.error('PDF generation error:', error);
      toast.error(error.response?.data?.detail || 'Fehler beim Generieren des Reports');
    } finally {
      setLoading(false);
    }
  };

  if (!session) {
    return (
      <div className="min-h-screen p-8 flex items-center justify-center" style={{ backgroundColor: '#0B0F14' }}>
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#1EA0E6] mx-auto mb-4"></div>
          <p style={{ color: '#94A3B8', fontSize: '1.125rem' }}>Diagnose wird geladen...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen p-8" style={{ backgroundColor: '#0B0F14' }}>
      {/* Disclaimer Modal */}
      {showDisclaimerModal && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ backgroundColor: 'rgba(0, 0, 0, 0.85)' }}
        >
          <div 
            className="max-w-2xl w-full rounded-xl p-6 shadow-2xl border"
            style={{ backgroundColor: '#1A1F26', borderColor: '#EF4444' }}
          >
            <div className="flex items-center gap-3 mb-4 pb-4 border-b" style={{ borderColor: '#374151' }}>
              <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{ backgroundColor: '#FEE2E2' }}>
                <span className="text-2xl">⚠️</span>
              </div>
              <div>
                <h3 className="text-xl font-bold" style={{ color: '#EF4444' }}>
                  Wichtiger Haftungsausschluss
                </h3>
                <p className="text-sm" style={{ color: '#9CA3AF' }}>
                  Bitte lesen und bestätigen Sie vor Beginn der Diagnose
                </p>
              </div>
            </div>

            <div 
              className="mb-6 p-4 rounded-lg overflow-y-auto"
              style={{ 
                backgroundColor: '#0F141A', 
                maxHeight: '400px',
                border: '1px solid #374151'
              }}
            >
              <div 
                className="text-sm leading-relaxed whitespace-pre-wrap"
                style={{ color: '#E5E7EB' }}
                dangerouslySetInnerHTML={{ __html: disclaimerText.replace(/\n/g, '<br/>') }}
              />
            </div>

            <div 
              className="mb-6 p-4 rounded-lg flex items-start gap-3"
              style={{ backgroundColor: '#FEF3C7', border: '1px solid #F59E0B' }}
            >
              <input
                type="checkbox"
                id="disclaimer-checkbox"
                checked={disclaimerCheckbox}
                onChange={(e) => setDisclaimerCheckbox(e.target.checked)}
                className="mt-1 w-5 h-5 rounded"
                style={{ accentColor: '#EF4444' }}
              />
              <label 
                htmlFor="disclaimer-checkbox" 
                className="text-sm font-medium cursor-pointer"
                style={{ color: '#78350F' }}
              >
                Ich habe den Haftungsausschluss vollständig gelesen und verstanden. 
                Mir ist bewusst, dass die KI-gestützte Diagnose keine professionelle Werkstattdiagnose ersetzt 
                und ich für die Durchführung der vorgeschlagenen Prüfschritte selbst verantwortlich bin.
              </label>
            </div>

            <div className="flex gap-3">
              <Button
                onClick={() => {
                  // Bei Ablehnung: Hinweis-Popup zeigen und zur Fahrzeugübersicht zurückleiten
                  toast.error('Diagnose abgebrochen. Sie müssen den Haftungsausschluss akzeptieren, um eine Diagnose durchzuführen.', {
                    duration: 5000,
                    icon: '❌'
                  });
                  navigate('/vehicles');
                }}
                variant="outline"
                className="flex-1"
                style={{ borderColor: '#6B7280', color: '#9CA3AF' }}
              >
                Ablehnen & Abbrechen
              </Button>
              <Button
                onClick={() => {
                  if (!disclaimerCheckbox) return;
                  // Sofort schließen für bessere UX
                  setShowDisclaimerModal(false);
                  setDisclaimerAccepted(true);
                  // Im Hintergrund speichern
                  handleAcceptDisclaimer();
                }}
                disabled={!disclaimerCheckbox}
                className="flex-1"
                style={{ 
                  backgroundColor: disclaimerCheckbox ? '#22C55E' : '#4B5563',
                  color: '#ffffff',
                  opacity: disclaimerCheckbox ? 1 : 0.5,
                  cursor: disclaimerCheckbox ? 'pointer' : 'not-allowed'
                }}
              >
                Akzeptieren & Fortfahren
              </Button>
            </div>
          </div>
        </div>
      )}

      <div className="max-w-4xl mx-auto">
        <h2 className="text-3xl font-bold mb-8" style={{ color: '#E6EDF3', fontFamily: 'Manrope' }}>
          Diagnose-Session
        </h2>

        {/* Vehicle Info Card with Image */}
        {session?.vehicle_id && (
          <Card className="mb-6" style={{ 
            backgroundColor: '#0F141A', 
            borderColor: 'rgba(30,160,230,0.3)',
            background: 'linear-gradient(135deg, rgba(30,160,230,0.08) 0%, #0F141A 100%)'
          }}>
            <CardContent className="p-4">
              <div className="flex items-center gap-4">
                <VehicleImage 
                  vehicleId={session.vehicle_id}
                  brand={session.vehicle?.brand}
                  model={session.vehicle?.model}
                  size="lg"
                />
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-cyan-400 mb-1">Fahrzeug</p>
                  <h3 className="text-lg font-semibold text-white truncate">
                    {session.vehicle?.brand} {session.vehicle?.model}
                  </h3>
                  {session.vehicle?.engine && (
                    <p className="text-sm text-gray-400 truncate">{session.vehicle.engine}</p>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        <Card className="mb-6" style={{ backgroundColor: '#0F141A', borderColor: '#1F2937' }}>
          <CardHeader>
            <CardTitle style={{ color: '#E6EDF3' }}>Symptom</CardTitle>
          </CardHeader>
          <CardContent>
            <p style={{ color: '#E6EDF3' }}>{session.symptom_text}</p>
            
            {/* File Upload bei Fehlerbeschreibung */}
            {userSubscription?.plan?.features?.file_uploads && !isComplete && (
              <div className="mt-4">
                <h4 className="font-bold mb-2 flex items-center gap-2" style={{ color: '#22D3EE' }}>
                  <span>📎</span> Bilder/Videos/Audio zum Fehler hochladen
                </h4>
                <p className="text-sm mb-3" style={{ color: '#94A3B8' }}>
                  Laden Sie Bilder, Videos oder Audioaufnahmen hoch, um den Fehler zu dokumentieren.
                </p>
                <FileUpload
                  sessionId={sessionId}
                  stepId={null}
                  onUploadComplete={(files) => {
                    setSessionAttachments([...sessionAttachments, ...files]);
                    toast.success('Dateien hochgeladen');
                  }}
                />
                {sessionAttachments.length > 0 && (
                  <FileAttachmentList
                    attachments={sessionAttachments}
                    onDelete={async (attachmentId) => {
                      try {
                        await axios.delete(`${API}/uploads/${attachmentId}`);
                        setSessionAttachments(sessionAttachments.filter(a => a.id !== attachmentId));
                        toast.success('Datei gelöscht');
                      } catch (error) {
                        toast.error('Fehler beim Löschen');
                      }
                    }}
                  />
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {isComplete ? (
          <div className="space-y-6">
            {/* Diagnose Card */}
            <Card style={{ backgroundColor: '#0F141A', borderColor: '#22C55E' }}>
              <CardHeader>
                <CardTitle style={{ color: '#22C55E' }}>✓ Diagnose abgeschlossen</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="p-4 rounded-lg" style={{ backgroundColor: '#22C55E20', borderLeft: '4px solid #22C55E' }}>
                  <h3 className="font-bold mb-2 flex items-center gap-2" style={{ color: '#22C55E' }}>
                    <span>🔍</span> Gefundenes Problem
                  </h3>
                  <p style={{ color: '#E6EDF3', fontSize: '1.1rem' }}>{finalDiagnosis?.diagnosis}</p>
                </div>
              </CardContent>
            </Card>

            {/* Reparaturanleitung Card - Strukturiert */}
            {finalDiagnosis?.repair && (() => {
              const sections = parseRepairGuide(finalDiagnosis.repair);
              return (
                <Card style={{ backgroundColor: '#0F141A', borderColor: '#1EA0E6' }}>
                  <CardHeader>
                    <CardTitle style={{ color: '#1EA0E6' }}>🔧 Detaillierte Reparaturanleitung</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    
                    {/* Sicherheitshinweise */}
                    {sections && sections.safety.length > 0 && (
                      <div className="p-4 rounded-lg" style={{ backgroundColor: '#F59E0B20', borderLeft: '4px solid #F59E0B' }}>
                        <h4 className="font-bold mb-3 flex items-center gap-2" style={{ color: '#F59E0B' }}>
                          <span>⚠️</span> Sicherheitshinweise
                        </h4>
                        <ul className="space-y-2" style={{ color: '#E6EDF3' }}>
                          {sections.safety.map((item, idx) => (
                            <li key={idx} className="flex items-start gap-2">
                              <span className="text-yellow-500">•</span>
                              <span>{item.replace(/^[-•*]\s*/, '')}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {/* Benötigte Werkzeuge */}
                    {sections && sections.tools.length > 0 && (
                      <div className="p-4 rounded-lg" style={{ backgroundColor: '#A78BFA20', borderLeft: '4px solid #A78BFA' }}>
                        <h4 className="font-bold mb-3 flex items-center gap-2" style={{ color: '#A78BFA' }}>
                          <span>🔧</span> Benötigte Werkzeuge
                        </h4>
                        <ul className="space-y-2" style={{ color: '#E6EDF3' }}>
                          {sections.tools.map((item, idx) => (
                            <li key={idx} className="flex items-start gap-2">
                              <span className="text-purple-400">•</span>
                              <span>{item.replace(/^[-•*]\s*/, '')}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {/* Benötigte Ersatzteile */}
                    {sections && sections.parts.length > 0 && (
                      <div className="p-4 rounded-lg" style={{ backgroundColor: '#22D3EE20', borderLeft: '4px solid #22D3EE' }}>
                        <h4 className="font-bold mb-3 flex items-center gap-2" style={{ color: '#22D3EE' }}>
                          <span>📦</span> Benötigte Ersatzteile
                        </h4>
                        <ul className="space-y-2" style={{ color: '#E6EDF3' }}>
                          {sections.parts.map((item, idx) => (
                            <li key={idx} className="flex items-start gap-2">
                              <span className="text-cyan-400">•</span>
                              <span>{item.replace(/^[-•*]\s*/, '')}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {/* Reparaturschritte */}
                    {sections && sections.steps.length > 0 && (
                      <div className="p-4 rounded-lg" style={{ backgroundColor: '#0B0F14', border: '1px solid #1F2937' }}>
                        <h4 className="font-bold mb-4 flex items-center gap-2" style={{ color: '#7CD4FD' }}>
                          <span>📋</span> Schritt-für-Schritt Anleitung
                        </h4>
                        <div className="space-y-3">
                          {sections.steps.map((step, idx) => (
                            <div key={idx} className="flex gap-3">
                              <div className="flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center" 
                                   style={{ backgroundColor: '#1EA0E6', color: '#ffffff', fontWeight: 'bold' }}>
                                {idx + 1}
                              </div>
                              <p style={{ color: '#E6EDF3', lineHeight: '1.8', paddingTop: '2px' }}>
                                {step.replace(/^\d+\.\s*/, '').replace(/^[-•*]\s*/, '')}
                              </p>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Hinweise und Tipps */}
                    {sections && sections.hints.length > 0 && (
                      <div className="p-4 rounded-lg" style={{ backgroundColor: '#7CD4FD20', borderLeft: '4px solid #7CD4FD' }}>
                        <h4 className="font-bold mb-3 flex items-center gap-2" style={{ color: '#7CD4FD' }}>
                          <span>💡</span> Wichtige Hinweise
                        </h4>
                        <ul className="space-y-2" style={{ color: '#E6EDF3' }}>
                          {sections.hints.map((item, idx) => (
                            <li key={idx} className="flex items-start gap-2">
                              <span className="text-cyan-400">•</span>
                              <span>{item.replace(/^[-•*]\s*/, '')}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {/* Sonstige Informationen */}
                    {sections && sections.other.length > 0 && (
                      <div className="p-4 rounded-lg" style={{ backgroundColor: '#1F2937', border: '1px solid #374151' }}>
                        <div className="space-y-2" style={{ color: '#E6EDF3' }}>
                          {sections.other.map((item, idx) => (
                            <p key={idx}>{item}</p>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Wenn keine Struktur erkannt wurde, zeige Original */}
                    {sections && !sections.safety.length && !sections.tools.length && 
                     !sections.parts.length && !sections.steps.length && !sections.other.length && (
                      <div className="p-4 rounded-lg" style={{ backgroundColor: '#1F2937', border: '1px solid #374151' }}>
                        <p style={{ color: '#E6EDF3', whiteSpace: 'pre-line', lineHeight: '1.8' }}>
                          {finalDiagnosis.repair}
                        </p>
                      </div>
                    )}

                  </CardContent>
                </Card>
              );
            })()}

            {/* Action Buttons */}
            <div className="flex gap-4">
              {!reportPdfUrl ? (
                <Button
                  onClick={handleGenerateReport}
                  disabled={loading}
                  style={{ backgroundColor: '#1EA0E6', color: '#ffffff' }}
                >
                  <FileText className="w-4 h-4 mr-2" />
                  {loading ? 'Generiere PDF...' : 'PDF Report generieren'}
                </Button>
              ) : (
                <Button 
                  onClick={async () => {
                    try {
                      const response = await axios.get(reportPdfUrl, {
                        responseType: 'blob'
                      });
                      
                      const blob = new Blob([response.data], { type: 'application/pdf' });
                      const url = window.URL.createObjectURL(blob);
                      const link = document.createElement('a');
                      link.href = url;
                      link.download = `diagnose_report_${sessionId}.pdf`;
                      document.body.appendChild(link);
                      link.click();
                      document.body.removeChild(link);
                      window.URL.revokeObjectURL(url);
                      
                      toast.success('PDF wird heruntergeladen...');
                    } catch (error) {
                      console.error('PDF download error:', error);
                      toast.error('Fehler beim Herunterladen des PDFs');
                    }
                  }}
                  style={{ backgroundColor: '#22C55E', color: '#ffffff' }}
                >
                  <FileText className="w-4 h-4 mr-2" />
                  ✓ PDF herunterladen
                </Button>
              )}
              <Button
                onClick={() => navigate('/dashboard')}
                variant="outline"
                style={{ borderColor: '#1F2937', color: '#E6EDF3' }}
              >
                Zum Dashboard
              </Button>
            </div>
          </div>
        ) : loading ? (
          <Card style={{ backgroundColor: '#0F141A', borderColor: '#1F2937' }}>
            <CardContent className="p-8 text-center">
              <Loader2 className="w-12 h-12 animate-spin mx-auto mb-4" style={{ color: '#7CD4FD' }} />
              <p style={{ color: '#94A3B8' }}>KI analysiert und generiert nächsten Schritt...</p>
            </CardContent>
          </Card>
        ) : currentStep ? (
          <Card style={{ backgroundColor: '#0F141A', borderColor: '#1F2937' }}>
            <CardHeader>
              <CardTitle style={{ color: '#E6EDF3' }}>Test durchführen</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Prüfschritt Title */}
              <div className="p-4 rounded-lg" style={{ backgroundColor: '#1EA0E620', borderLeft: '4px solid #1EA0E6' }}>
                <h3 className="text-xl font-bold" style={{ color: '#1EA0E6' }}>
                  {currentStep.instruction}
                </h3>
              </div>

              {/* Explanation */}
              {currentStep.explanation && (
                <div className="p-4 rounded-lg" style={{ backgroundColor: '#7CD4FD20', borderLeft: '4px solid #7CD4FD' }}>
                  <h4 className="font-bold mb-2 flex items-center gap-2" style={{ color: '#7CD4FD' }}>
                    <span>💡</span> Warum diese Prüfung?
                  </h4>
                  <p style={{ color: '#E6EDF3' }}>{currentStep.explanation}</p>
                </div>
              )}

              {/* Safety */}
              {currentStep.safety && (
                <div className="p-4 rounded-lg" style={{ backgroundColor: '#F59E0B20', borderLeft: '4px solid #F59E0B' }}>
                  <h4 className="font-bold mb-2 flex items-center gap-2" style={{ color: '#F59E0B' }}>
                    <span>⚠️</span> Sicherheitshinweise
                  </h4>
                  <p style={{ color: '#E6EDF3', whiteSpace: 'pre-line' }}>{currentStep.safety}</p>
                </div>
              )}

              {/* Location */}
              {currentStep.location && (
                <div className="p-4 rounded-lg" style={{ backgroundColor: '#22C55E20', borderLeft: '4px solid #22C55E' }}>
                  <h4 className="font-bold mb-2 flex items-center gap-2" style={{ color: '#22C55E' }}>
                    <span>📍</span> Lokalisierung
                  </h4>
                  <p style={{ color: '#E6EDF3', whiteSpace: 'pre-line' }}>{currentStep.location}</p>
                </div>
              )}

              {/* Preparation */}
              {currentStep.preparation && (
                <div className="p-4 rounded-lg" style={{ backgroundColor: '#A78BFA20', borderLeft: '4px solid #A78BFA' }}>
                  <h4 className="font-bold mb-2 flex items-center gap-2" style={{ color: '#A78BFA' }}>
                    <span>🔧</span> Vorbereitung
                  </h4>
                  <p style={{ color: '#E6EDF3', whiteSpace: 'pre-line' }}>{currentStep.preparation}</p>
                </div>
              )}

              {/* Procedure */}
              {currentStep.procedure && (
                <div className="p-4 rounded-lg" style={{ backgroundColor: '#0B0F14', border: '1px solid #1F2937' }}>
                  <h4 className="font-bold mb-3" style={{ color: '#7CD4FD' }}>
                    📋 Prüfprozedur
                  </h4>
                  <div style={{ color: '#E6EDF3', whiteSpace: 'pre-line', lineHeight: '1.8' }}>
                    {currentStep.procedure}
                  </div>
                </div>
              )}

              {/* Expected Result */}
              {currentStep.expected && (
                <div className="p-4 rounded-lg" style={{ backgroundColor: '#22D3EE20', borderLeft: '4px solid #22D3EE' }}>
                  <h4 className="font-bold mb-2 flex items-center gap-2" style={{ color: '#22D3EE' }}>
                    <span>✓</span> Zu erwartendes Ergebnis
                  </h4>
                  <p style={{ color: '#E6EDF3', whiteSpace: 'pre-line' }}>{currentStep.expected}</p>
                  {currentStep.thresholds && (
                    <div className="mt-2 text-sm" style={{ color: '#94A3B8' }}>
                      Sollwerte: {currentStep.thresholds.min}-{currentStep.thresholds.max} {currentStep.thresholds.unit}
                    </div>
                  )}
                </div>
              )}

              {/* File Upload Feature (Premium) */}
              {userSubscription?.plan?.features?.file_uploads && (
                <div className="p-4 rounded-lg mb-4" style={{ backgroundColor: '#22D3EE20', borderLeft: '4px solid #22D3EE' }}>
                  <h4 className="font-bold mb-3 flex items-center gap-2" style={{ color: '#22D3EE' }}>
                    <span>📎</span> Bilder/Videos/Audio hochladen
                  </h4>
                  <p className="text-sm mb-3" style={{ color: '#94A3B8' }}>
                    Laden Sie Bilder, Videos oder Audioaufnahmen hoch, um den Fehler zu dokumentieren.
                  </p>
                  <FileUpload
                    sessionId={sessionId}
                    stepId={currentStep?.id}
                    onUploadComplete={(files) => {
                      setSessionAttachments([...sessionAttachments, ...files]);
                      toast.success('Dateien hochgeladen');
                    }}
                  />
                </div>
              )}

              {/* Live OBD Feature (Premium) */}
              {userSubscription?.plan?.features?.live_obd_enabled && (
                <div className="p-4 rounded-lg mb-4" style={{ 
                  backgroundColor: obdAgentStatus === 'connected' ? '#22C55E20' : '#F5922010', 
                  borderLeft: `4px solid ${obdAgentStatus === 'connected' ? '#22C55E' : '#F59220'}` 
                }}>
                  <h4 className="font-bold mb-3 flex items-center gap-2" style={{ color: obdAgentStatus === 'connected' ? '#22C55E' : '#F59220' }}>
                    <Activity className="w-5 h-5" />
                    Live OBD-Daten vom Fahrzeug
                  </h4>
                  
                  {/* Connection Status */}
                  <div className="flex items-center gap-2 mb-4 text-sm">
                    {obdAgentStatus === 'checking' ? (
                      <>
                        <RefreshCw className="w-4 h-4 animate-spin text-gray-400" />
                        <span style={{ color: '#94A3B8' }}>Prüfe Verbindung...</span>
                      </>
                    ) : obdAgentStatus === 'connected' ? (
                      <>
                        <Wifi className="w-4 h-4 text-green-400" />
                        <span style={{ color: '#22C55E' }}>OBD-Agent verbunden</span>
                      </>
                    ) : (
                      <>
                        <WifiOff className="w-4 h-4 text-orange-400" />
                        <span style={{ color: '#F59220' }}>OBD-Agent nicht verbunden</span>
                        <Button 
                          size="sm" 
                          variant="outline" 
                          onClick={checkObdAgentStatus}
                          className="ml-2"
                        >
                          <RefreshCw className="w-3 h-3 mr-1" />
                          Erneut prüfen
                        </Button>
                      </>
                    )}
                  </div>
                  
                  {obdAgentStatus === 'connected' && (
                    <>
                      <p className="text-sm mb-4" style={{ color: '#94A3B8' }}>
                        Lesen Sie Fehlercodes oder Live-Sensordaten direkt vom Fahrzeug aus. Die Daten werden automatisch in den KI-Kontext übernommen.
                      </p>
                      
                      <div className="flex flex-wrap gap-3">
                        <Button
                          onClick={requestLiveDtcData}
                          disabled={liveDataLoading}
                          className="bg-gradient-to-r from-[#22C55E] to-[#10B981] hover:opacity-90"
                        >
                          {liveDataLoading ? (
                            <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Lese aus...</>
                          ) : (
                            <><Activity className="w-4 h-4 mr-2" /> Fehlercodes auslesen</>
                          )}
                        </Button>
                        
                        <Button
                          onClick={requestLiveSensorData}
                          disabled={liveDataLoading}
                          className="bg-gradient-to-r from-[#7CD4FD] to-[#3B82F6] hover:opacity-90"
                        >
                          {liveDataLoading ? (
                            <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Lese aus...</>
                          ) : (
                            <><Activity className="w-4 h-4 mr-2" /> Live-Sensordaten</>
                          )}
                        </Button>
                      </div>
                      
                      {/* Display Live DTC Results */}
                      {liveDtcData && (
                        <div className="mt-4 p-3 rounded-lg bg-black/30">
                          <h5 className="text-sm font-semibold text-white mb-2">Fehlercodes vom Fahrzeug:</h5>
                          {liveDtcData.dtcs?.length > 0 ? (
                            <div className="space-y-1">
                              {liveDtcData.dtcs.map((dtc, i) => (
                                <div key={i} className="flex items-center gap-2 text-sm">
                                  <span className="text-red-400 font-mono">{dtc.code}</span>
                                  <span className="text-gray-400">{dtc.description}</span>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <p className="text-green-400 text-sm">✓ Keine aktiven Fehlercodes</p>
                          )}
                        </div>
                      )}
                      
                      {/* Display Live Sensor Data */}
                      {liveData && liveData.values && (
                        <div className="mt-4 p-3 rounded-lg bg-black/30">
                          <h5 className="text-sm font-semibold text-white mb-2">Live-Sensordaten:</h5>
                          <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                            {Object.entries(liveData.values).map(([pid, data]) => (
                              <div key={pid} className="p-2 rounded bg-gray-800/50">
                                <span className="text-xs text-gray-400 block">{data.name || pid}</span>
                                <span className="text-lg font-bold text-cyan-400">
                                  {data.value} <span className="text-sm text-gray-400">{data.unit}</span>
                                </span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </>
                  )}
                  
                  {obdAgentStatus === 'disconnected' && (
                    <p className="text-sm" style={{ color: '#94A3B8' }}>
                      Um Live-Daten vom Fahrzeug auszulesen, verbinden Sie bitte den CarCortex OBD-Agent mit Ihrem Fahrzeug.
                      <a href="/obd-live" className="text-cyan-400 hover:underline ml-1">Zur OBD-Live Seite →</a>
                    </p>
                  )}
                </div>
              )}

              {/* Istwert-Prüfung Feature (Premium) - Mit Multi-Value Support */}
              {userSubscription?.plan?.features?.actual_value_assessment && currentStep.expected && (
                <div className="p-4 rounded-lg" style={{ backgroundColor: '#A78BFA20', borderLeft: '4px solid #A78BFA' }}>
                  <h4 className="font-bold mb-3 flex items-center gap-2" style={{ color: '#A78BFA' }}>
                    <span>⚡</span> Istwert-Prüfung & automatische Beurteilung
                  </h4>
                  <p className="text-sm mb-4" style={{ color: '#94A3B8' }}>
                    Geben Sie Ihre gemessenen Werte ein. Das System beurteilt automatisch, ob die Werte im Sollbereich liegen.
                    Sie können mehrere Messwerte gleichzeitig erfassen (z.B. Widerstand UND Spannung).
                  </p>
                  
                  <div className="space-y-3">
                    {/* Mehrere Messzeilen */}
                    {actualValues.map((measurement, index) => (
                      <div key={index} className="flex gap-3 items-center">
                        <input
                          type="text"
                          value={measurement.value}
                          onChange={(e) => updateMeasurementValue(index, 'value', e.target.value)}
                          placeholder="z.B. 1.5 oder 12.5"
                          className="flex-1 px-4 py-2 rounded-lg"
                          style={{ 
                            backgroundColor: '#0B0F14', 
                            borderColor: '#1F2937', 
                            color: '#E6EDF3', 
                            border: '1px solid' 
                          }}
                        />
                        
                        {/* Dropdown für Einheit */}
                        <select
                          value={measurement.unit}
                          onChange={(e) => updateMeasurementValue(index, 'unit', e.target.value)}
                          className="px-4 py-2 rounded-lg"
                          style={{ 
                            backgroundColor: '#0B0F14', 
                            borderColor: '#1F2937', 
                            color: '#E6EDF3', 
                            border: '1px solid',
                            minWidth: '100px'
                          }}
                        >
                          {availableUnits.length > 0 ? (
                            availableUnits.map(unit => (
                              <option key={unit} value={unit}>{unit}</option>
                            ))
                          ) : (
                            <>
                              <option value="V">V (Volt)</option>
                              <option value="A">A (Ampere)</option>
                              <option value="Ω">Ω (Ohm)</option>
                              <option value="kΩ">kΩ (Kiloohm)</option>
                              <option value="mV">mV (Millivolt)</option>
                              <option value="mA">mA (Milliampere)</option>
                              <option value="ml">ml (Milliliter)</option>
                              <option value="bar">bar</option>
                              <option value="Hz">Hz (Hertz)</option>
                              <option value="°C">°C (Grad)</option>
                              <option value="%">% (Prozent)</option>
                            </>
                          )}
                        </select>

                        {/* Entfernen-Button (nur wenn mehr als 1 Zeile) */}
                        {actualValues.length > 1 && (
                          <Button
                            onClick={() => removeMeasurementRow(index)}
                            variant="outline"
                            size="sm"
                            style={{ 
                              borderColor: '#EF4444', 
                              color: '#EF4444',
                              minWidth: '40px'
                            }}
                          >
                            ✕
                          </Button>
                        )}
                      </div>
                    ))}

                    {/* Buttons */}
                    <div className="flex gap-3">
                      <Button
                        onClick={addMeasurementRow}
                        variant="outline"
                        style={{ 
                          borderColor: '#A78BFA', 
                          color: '#A78BFA',
                          flex: '1'
                        }}
                      >
                        + Weiterer Messwert
                      </Button>
                      
                      <Button
                        onClick={assessActualValue}
                        disabled={!actualValues.some(m => m.value.trim()) || loading}
                        style={{ 
                          backgroundColor: '#A78BFA', 
                          color: '#ffffff',
                          flex: '1'
                        }}
                      >
                        {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Alle Werte beurteilen'}
                      </Button>
                    </div>
                    
                    {/* Assessment Result */}
                    {valueAssessment && (
                      <div className={`p-4 rounded-lg ${
                        valueAssessment.status === 'ok' ? 'bg-green-500/20' : 
                        valueAssessment.status === 'warning' ? 'bg-yellow-500/20' : 
                        'bg-red-500/20'
                      }`} style={{ border: `1px solid ${
                        valueAssessment.status === 'ok' ? '#22C55E' : 
                        valueAssessment.status === 'warning' ? '#F59E0B' : 
                        '#EF4444'
                      }` }}>
                        <div className="flex items-center gap-3 mb-2">
                          {valueAssessment.status === 'ok' ? (
                            <CheckCircle className="w-6 h-6 text-green-500" />
                          ) : valueAssessment.status === 'warning' ? (
                            <HelpCircle className="w-6 h-6 text-yellow-500" />
                          ) : (
                            <XCircle className="w-6 h-6 text-red-500" />
                          )}
                          <span className="font-bold" style={{ color: '#E6EDF3' }}>
                            {valueAssessment.status === 'ok' ? '✓ Wert OK' : 
                             valueAssessment.status === 'warning' ? '⚠ Grenzwertig' : 
                             '✗ Wert außerhalb Sollbereich'}
                          </span>
                        </div>
                        <p style={{ color: '#E6EDF3' }}>{valueAssessment.assessment}</p>
                        {valueAssessment.recommendation && (
                          <div className="mt-3 pt-3" style={{ borderTop: '1px solid #1F2937' }}>
                            <p className="text-sm font-semibold mb-1" style={{ color: '#7CD4FD' }}>Empfehlung:</p>
                            <p className="text-sm" style={{ color: '#E6EDF3' }}>{valueAssessment.recommendation}</p>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {currentStep.source_refs && currentStep.source_refs.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium mb-2" style={{ color: '#94A3B8' }}>Quellen:</h4>
                  <div className="space-y-1">
                    {currentStep.source_refs.map((ref, idx) => (
                      <a
                        key={idx}
                        href={ref.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm block hover:underline"
                        style={{ color: '#7CD4FD' }}
                      >
                        {ref.title}
                      </a>
                    ))}
                  </div>
                </div>
              )}

              <div>
                <label className="block mb-2" style={{ color: '#E6EDF3' }}>Notiz (optional):</label>
                <Textarea
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  placeholder="Zusätzliche Beobachtungen..."
                  rows={3}
                  style={{ backgroundColor: '#0B0F14', borderColor: '#1F2937', color: '#E6EDF3' }}
                />
              </div>

              <div>
                <p className="mb-3 font-medium" style={{ color: '#E6EDF3' }}>Wie ist das Ergebnis der Prüfung?</p>
                <div className="grid grid-cols-3 gap-3">
                  <Button
                    onClick={() => handleResult('good')}
                    className="h-24 flex flex-col items-center justify-center text-center"
                    style={{ backgroundColor: '#22C55E', color: '#ffffff' }}
                  >
                    <CheckCircle className="w-8 h-8 mb-2" />
                    <span className="text-sm font-medium">Bauteil i.O.</span>
                    <span className="text-xs opacity-80">Kein Defekt</span>
                  </Button>
                  <Button
                    onClick={() => handleResult('bad')}
                    className="h-24 flex flex-col items-center justify-center text-center"
                    style={{ backgroundColor: '#EF4444', color: '#ffffff' }}
                  >
                    <XCircle className="w-8 h-8 mb-2" />
                    <span className="text-sm font-medium">Bauteil defekt</span>
                    <span className="text-xs opacity-80">Fehler gefunden</span>
                  </Button>
                  <Button
                    onClick={() => handleResult('unclear')}
                    className="h-24 flex flex-col items-center justify-center text-center"
                    style={{ backgroundColor: '#F59E0B', color: '#ffffff' }}
                  >
                    <HelpCircle className="w-8 h-8 mb-2" />
                    <span className="text-sm font-medium">Unsicher</span>
                    <span className="text-xs opacity-80">Kann nicht beurteilen</span>
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        ) : null}
      </div>

      {/* Feedback Dialog */}
      <FeedbackDialog 
        open={showFeedbackDialog}
        onClose={() => setShowFeedbackDialog(false)}
        sessionId={sessionId}
      />
    </div>
  );
};